import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, Sparkles, BadgeCheck, ChefHat } from "lucide-react";
import { useMasters, PillSelect, Chips } from "../form/engine";
import { formatApiError } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["9 AM - 12 PM", "12 PM - 3 PM", "3 PM - 6 PM", "6 PM - 10 PM"];

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const ChefRiderPanel = () => {
  const masters = useMasters();
  const [cuisine, setCuisine] = useState("");
  const [slot, setSlot] = useState("");
  const [date, setDate] = useState("");
  const [address, setAddress] = useState("");
  const [packageId, setPackageId] = useState("family_dinner");
  const [matches, setMatches] = useState(null);
  const [packages, setPackages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [bookingChef, setBookingChef] = useState(null);

  const loadBookings = () => {
    axios.get(`${API}/chefrider/bookings/mine`, headers()).then((r) => setBookings(r.data.bookings)).catch(() => {});
  };
  useEffect(loadBookings, []);

  const findChefs = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (cuisine) params.set("cuisine", cuisine);
      if (slot) params.set("slot", slot);
      const { data } = await axios.get(`${API}/chefrider/chefs?${params}`, headers());
      setMatches(data.chefs);
      setPackages(data.packages);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const book = async (chefId) => {
    setBookingChef(chefId);
    try {
      const { data } = await axios.post(
        `${API}/chefrider/book`,
        { chef_id: chefId, package_id: packageId, cuisine, date, slot, address },
        headers()
      );
      toast.success(`${data.chef} booked · ₹${data.price} 🎉 You'll be pinged on acceptance.`);
      loadBookings();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBookingChef(null);
    }
  };

  const cancel = async (id) => {
    try {
      await axios.patch(`${API}/chefrider/bookings/${id}/status`, { status: "cancelled" }, headers());
      toast.success("Booking cancelled");
      loadBookings();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const pkg = packages.find((p) => p.id === packageId);

  return (
    <div className="space-y-6" data-testid="panel-chefrider">
      <div className="rounded-3xl border border-[#F97316]/25 bg-[#F97316]/[0.05] p-6">
        <p className="flex items-center gap-2 font-display text-lg font-bold">
          <ChefHat className="h-5 w-5 text-[#F97316]" /> Book a home chef
        </p>
        <p className="mt-1 text-xs text-slate-400">
          Rapido for chefs — certified chefs come home and cook live. Pick your vibe, FUNZI matches.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <PillSelect label="Cuisine" options={masters.cuisines || []} value={cuisine} onChange={setCuisine} testid="cr-cuisine" />
          <PillSelect label="Time Slot" options={SLOTS} value={slot} onChange={setSlot} testid="cr-slot" />
          <div>
            <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Date</p>
            <input
              data-testid="cr-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white outline-none focus:border-[#F97316]/60 [color-scheme:dark]"
            />
          </div>
          <div>
            <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Your Address</p>
            <input
              data-testid="cr-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Flat / hostel / area"
              className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#F97316]/60"
            />
          </div>
        </div>
        <div className="mt-4">
          <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Package</p>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3" data-testid="cr-packages">
            {(packages.length ? packages : [
              { id: "family_dinner", name: "Family Dinner", guests: 4, price: 999, emoji: "🍽️" },
              { id: "chef_experience", name: "Chef Experience", guests: 10, price: 4999, emoji: "✨" },
              { id: "party_catering", name: "Party Catering", guests: 50, price: 24999, emoji: "🎉" },
            ]).map((p) => (
              <button
                key={p.id}
                data-testid={`cr-package-${p.id}`}
                onClick={() => setPackageId(p.id)}
                className={`rounded-2xl border p-4 text-left transition-all ${
                  packageId === p.id ? "border-[#F97316] bg-[#F97316]/10" : "border-white/10 bg-white/[0.04] hover:border-white/30"
                }`}
              >
                <span className="text-xl">{p.emoji}</span>
                <p className="mt-1.5 text-sm font-bold">{p.name}</p>
                <p className="font-jbmono text-[10px] text-slate-500">{p.guests} guests · ₹{p.price.toLocaleString()}</p>
              </button>
            ))}
          </div>
        </div>
        <button
          data-testid="cr-find"
          onClick={findChefs}
          disabled={busy || !slot}
          className="mt-5 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-7 py-3 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50"
        >
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          FUNZI, find my chef
        </button>
      </div>

      {matches !== null && (
        <div data-testid="cr-matches">
          <p className="font-jbmono text-[10px] uppercase tracking-widest text-[#06B6D4]">
            FUNZI matched {matches.length} chef{matches.length === 1 ? "" : "s"}
          </p>
          {matches.length === 0 ? (
            <p className="mt-3 rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center text-sm text-slate-500">
              No chefs free in that slot — try another time.
            </p>
          ) : (
            <div className="mt-3 space-y-3">
              {matches.map((c) => (
                <div key={c.id} data-testid={`cr-chef-${c.id.slice(-6)}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F97316] to-[#22C55E] text-xl">👨‍🍳</span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm font-bold">
                      {c.name}
                      {c.certified && (
                        <span className="flex items-center gap-1 rounded-full bg-[#06B6D4]/10 px-2.5 py-0.5 font-jbmono text-[9px] font-bold uppercase tracking-wider text-[#06B6D4]">
                          <BadgeCheck className="h-3 w-3" /> FUNZ Certified
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 font-jbmono text-[10px] text-slate-500">
                      {c.chef_type} · {c.cuisines} · {c.experience}
                    </p>
                    <p className="mt-1 font-jbmono text-[10px] text-[#06B6D4]">{c.match_reason}</p>
                  </div>
                  <button
                    data-testid={`cr-book-${c.id.slice(-6)}`}
                    onClick={() => book(c.id)}
                    disabled={bookingChef === c.id || !date || !address.trim()}
                    className="rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50"
                  >
                    {bookingChef === c.id ? "…" : `Book · ₹${(pkg?.price || 999).toLocaleString()}`}
                  </button>
                </div>
              ))}
            </div>
          )}
          {(!date || !address.trim()) && (
            <p className="mt-2 font-jbmono text-[10px] uppercase tracking-widest text-[#22C55E]">Add date + address above to enable booking</p>
          )}
        </div>
      )}

      {bookings.length > 0 && (
        <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6" data-testid="cr-bookings">
          <p className="font-display text-lg font-bold">Your chef bookings</p>
          <div className="mt-4 space-y-2.5">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`cr-booking-${b.id.slice(-6)}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold">{b.package} · {b.chef_name}</p>
                  <p className="font-jbmono text-[10px] text-slate-500">{b.date} · {b.slot} · {b.guests} guests</p>
                </div>
                <span className={`rounded-full px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-wider ${
                  b.status === "accepted" ? "bg-[#06B6D4]/10 text-[#06B6D4]" :
                  b.status === "completed" ? "bg-[#7C3AED]/15 text-[#A78BFA]" :
                  b.status === "cancelled" ? "bg-white/5 text-slate-500" : "bg-[#22C55E]/10 text-[#22C55E]"
                }`}>
                  {b.status}
                </span>
                <span className="font-jbmono text-xs font-bold text-[#22C55E]">₹{b.price.toLocaleString()}</span>
                {b.status === "requested" && (
                  <button data-testid={`cr-cancel-${b.id.slice(-6)}`} onClick={() => cancel(b.id)} className="rounded-full border border-white/15 px-3 py-1.5 text-[10px] font-bold text-slate-400 hover:text-[#F97316]">
                    Cancel
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
